import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, MapPin, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/Shared';
import { PinPlacer } from '../components/PinPlacer';
import { api } from '../lib/api';
import { toast } from 'sonner';

/* Reporter-side pin adjustment (Phase 112). Reached only through the
   token link on the confirmation screen / receipt email. */
export default function AdjustPin() {
  const [params] = useSearchParams();
  const track = (params.get('track') || '').trim();
  const token = (params.get('token') || '').trim();
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [pin, setPin] = useState(null);
  const [exactPublic, setExactPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');

  useEffect(() => {
    if (!track || !token) { setError('This link is missing its tracking number or key. Use the link from your confirmation screen or receipt email.'); return; }
    api.get('/issues/pin', { params: { track, token } })
      .then((r) => {
        setState(r.data);
        if (r.data.exact_lat != null) setPin({ lat: r.data.exact_lat, lng: r.data.exact_lng });
        setExactPublic(!!r.data.public_pin_exact);
      })
      .catch((e) => setError(e.response?.data?.detail || 'This link is not valid.'));
  }, [track, token]);

  const save = async () => {
    if (!pin) return;
    setSaving(true);
    try {
      const r = await api.post('/issues/pin', { track, token, lat: pin.lat, lng: pin.lng, exact_public: exactPublic });
      setState(r.data);
      setSaved(r.data.message || 'Your pin has been moved.');
      toast.success('Pin updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not move the pin');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader kicker="YOUR REPORT" title="Put the pin where the issue really is" subtitle="Zoom in, click the exact spot, or drag the pin. Your exact address is never shown publicly." />
      <div className="max-w-3xl mx-auto px-4 py-10 space-y-5" data-testid="adjust-pin-page">
        {error && (
          <Card className="border-amber-200 bg-amber-50" data-testid="adjust-pin-error">
            <CardContent className="p-6">
              <p className="font-serif font-bold text-amber-900">We cannot open this pin</p>
              <p className="text-sm text-amber-900 mt-1.5">{error}</p>
              <Button asChild variant="outline" className="mt-4 h-11 font-bold btn-press"><Link to="/issue-tracker/track">Track My Issue</Link></Button>
            </CardContent>
          </Card>
        )}
        {!error && !state && <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
        {state && (
          <Card data-testid="adjust-pin-card">
            <CardContent className="p-6 sm:p-8 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Tracking number</p>
                  <p className="font-mono text-xl font-black text-[#1a2744]" data-testid="adjust-pin-tracking">{state.tracking_number}</p>
                </div>
                <div className="text-right text-xs text-slate-600">
                  {state.location && <p data-testid="adjust-pin-location-text">Described as: <strong>{state.location}</strong>{state.community ? ` (${state.community})` : ''}</p>}
                  <p data-testid="adjust-pin-ward">{state.ward ? `Currently recorded in Ward ${state.ward}` : state.needs_ward_confirmation ? 'Ward to be confirmed by Steve (boundary)' : 'Ward not yet determined'}</p>
                </div>
              </div>
              {state.exact_lat == null && (
                <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="adjust-pin-none">No exact spot has been recorded yet. Place the pin below so your report shows in the right place.</p>
              )}
              <PinPlacer value={pin} onChange={setPin} exactPublic={exactPublic} onExactPublicChange={setExactPublic} testId="adjust-pin-placer" height="h-80" />
              {saved && (
                <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 inline-flex items-start gap-2" data-testid="adjust-pin-saved"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> {saved}</p>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={save} disabled={!pin || saving} className="h-12 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press" data-testid="adjust-pin-save">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MapPin className="h-4 w-4 mr-2" />} Save pin location
                </Button>
                <Button asChild variant="outline" className="h-12 font-bold btn-press" data-testid="adjust-pin-view-map"><Link to="/map">View the public map</Link></Button>
              </div>
              <p className="text-xs text-slate-500 inline-flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Only you (with this link) and Steve can move this pin. The public map shows the pin about 100 to 150 m from a private address unless you mark a public place.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
